<template>
  <div>
    <standard-cart v-if="order.groupOrder.you.isHost && order.groupOrder.isLocked">
      <template #header>
        <button
          class="link link--has-title"
          type="button"
          tabindex="-1"
          :disabled="isUnlockingOrder"
          @click="unlockOrder"
        >
          <div class="layout layout--icon layout--icon-extra-spacing">
            <div
              v-if="isUnlockingOrder"
              class="loader loader--medium dark-gray"
            />
            <span
              v-else
              class="icon icon--smedium far fa-arrow-left dark-gray"
            />
            <h2 class="title title--2">
              Checkout
            </h2>
          </div>
        </button>
      </template>
    </standard-cart>



    <template v-else>
      <div class="section section--half-y-padding">
        <h2 class="title title--2">
          Group Order
        </h2>
      </div>

      <div class="section section--half-y-padding section--alt-background">
        <div class="layout layout--horizontal">
          <div class="avatar avatar--primary">
            {{ order.groupOrder.you.displayNameInitials }}
          </div>
          <h3 class="title title--5 grow">
            {{ order.groupOrder.you.displayName }} <span class="dark-gray regular smaller">(Your Order)</span>
          </h3>
        </div>

        <template v-if="order.groupOrder.you.orderItems.length > 0">
          <order-items
            :hide-prices="selectedLocation.features.HidePrices"
            :disable-favoriting="true"
            :hide-totals="true"
            :hide-quantity="true"
            :order="order"
            :order-items="order.groupOrder.you.orderItems"
            :outside-borders="true"
            :read-only="order.groupOrder.isLocked"
          />

          <div
            v-show="!selectedLocation.features.HidePrices"
            class="layout layout--horizontal"
          >
            <p class="bigger">
              Your Subtotal
            </p>
            <p class="bigger bold dark">
              {{ $dollars(order.groupOrder.you.subtotal) }}
            </p>
          </div>
        </template>

        <template v-else>
          <p>
            You haven't added any items yet. Browse our menu and start adding some tasty items to your cart.
          </p>

          <button
            class="button button--primary button--large button--auto-width"
            type="button"
            @click="browseMenu"
          >
            Browse Menu
          </button>
        </template>
      </div>

      <div class="section section--half-y-padding">
        <expandable-section>
          <template #title>
            <h3 class="title title--5">
              See what others are ordering
            </h3>
          </template>

          <template #content>
            <div
              v-if="order.groupOrder.otherMembers.length === 0"
              class="layout layout--horizontal"
            >
              <p class="grow">
                Nobody has joined your group order yet.
              </p>
              <button
                class="button button--small button--secondary button--auto-width"
                type="button"
                @click="$openModal('editGroupOrder')"
              >
                Invite Friends
              </button>
            </div>

            <div
              v-for="(member, index) in order.groupOrder.otherMembers"
              :key="index"
            >
              <div class="layout layout--horizontal">
                <div class="avatar">
                  {{ member.displayNameInitials }}
                </div>

                <h4 class="title title--6 dark grow">
                  {{ member.displayName }} <span
                    v-if="member.isHost"
                    class="smaller regular dark-gray"
                  >(Host)</span>

                  <button
                    v-if="order.groupOrder.you.isHost"
                    class="link link--remove danger"
                    type="button"
                    @click="removeMemberFromOrder(member)"
                  >
                    Remove
                  </button>
                </h4>

                <p
                  v-show="!selectedLocation.features.HidePrices"
                  class="dark bold"
                >
                  {{ $dollars(member.subtotal) }}
                </p>
              </div>

              <order-items
                v-if="member.orderItems.length > 0"
                :hide-prices="selectedLocation.features.HidePrices"
                :disable-favoriting="true"
                :hide-totals="true"
                :hide-quantity="true"
                :order="order"
                :order-items="member.orderItems"
                :outside-borders="true"
                :read-only="!order.groupOrder.you.isHost"
              />
            </div>
          </template>
        </expandable-section>
      </div>

      <div
        v-if="order.orderItems.length > 0 && !selectedLocation.features.HidePrices"
        class="section section--half-y-padding"
      >
        <div class="layout layout--horizontal">
          <p class="title title--2 regular dark-gray">
            Group Subtotal
          </p>
          <p class="title title--2 bold dark">
            {{ $dollars(order.subtotal) }}
          </p>
        </div>
      </div>

      <div
        v-if="order.groupOrder.you.isHost && !orderValidations.isValid"
        class="section section--half-y-padding"
      >
        <div class="message message--danger">
          <p
            v-for="error in orderValidations.errors"
            :key="error"
          >
            {{ error }}
          </p>
        </div>
      </div>

      <div
        v-if="order.groupOrder.you.isHost"
        class="section section--half-y-padding section--alt-background center"
      >
        <div class="layout layout--horizontal layout--horizontal-equal">
          <button
            class="link link--margin danger"
            type="button"
            @click="cancelOrder"
          >
            Cancel Order
          </button>

          <button
            v-if="order.orderItems.length > 0 && orderValidations.isValid"
            class="button button--large button--primary"
            type="button"
            @click="checkout"
          >
            Checkout
          </button>
        </div>
      </div>


      <div
        v-if="!order.groupOrder.you.isHost"
        class="section section--half-y-padding section--alt-background"
      >
        <group-order-locked-message v-if="order.groupOrder.isLocked" />

        <div
          v-else
          class="layout layout--horizontal"
        >
          <p class="smaller grow">
            {{ order.groupOrder.host.displayName }} (the host) will checkout and submit the order once everyone <span class="no-wrap">is ready.</span>
          </p>

          <button
            class="button button--light button--danger button--auto-width button--small"
            type="button"
            @click="leaveOrder"
          >
            Leave Group
          </button>
        </div>
      </div>
    </template>
  </div>
</template>



<script>
  import errorHandlers from '@/services/error-handlers';
  import events from '@/services/events';
  import StandardCart from './standard-cart.vue';
  import { mapGetters } from 'vuex';



  export default {
    name: 'GroupCart',

    components: {
      StandardCart
    },

    data() {
      return {
        isUnlockingOrder: false
      };
    },

    computed: {
      ...mapGetters('locations', ['selectedLocation']),
      ...mapGetters('orders', ['order', 'orderValidations'])
    },

    created() {
      this.createdHandler();
    },

    unmounted() {
      this.destroyedHandler();
    },

    methods: {
      browseMenu() {
        this.$closeModal('cart');

        this.$router.push({
          name: 'orderMenu',
          params: {
            merchantId: this.$route.params.merchantId
          }
        });
      },

      cancelOrder() {
        this.$openModal('confirmation', {
          ctaConfirmText: 'Yes, Cancel',
          primaryText: 'All items and guests will be removed from your cart.',
          onConfirm: () => this.$store.dispatch('orders/cancelOrder'),
          onConfirmSuccess: () => {
            this.$closeAllModals();
          },
          title: 'Cancel group order?'
        });
      },

      checkout() {
        this.$openModal('confirmation', {
          ctaConfirmText: 'Lock Order',
          primaryText: 'By proceeding to checkout, your group\'s order will be locked - not allowing further changes. You can exit the checkout process to unlock your order.',
          onConfirm: () => this.$store.dispatch('orders/groupOrderUpdateLock', { isLocked: true }),
          title: 'Lock group order?'
        });
      },

      createdHandler() {
        this.loadUpdatedOrder();
      },

      destroyedHandler() {
        this.unlockOrder();
      },

      leaveOrder() {
        this.$openModal('confirmation', {
          ctaConfirmText: 'Yes, Leave',
          primaryText: 'Your items in the group cart will be removed and your cart will be cleared.',
          onConfirm: () => this.$store.dispatch('orders/groupOrderLeave'),
          onConfirmSuccess: () => {
            this.$closeModal('cart');

            // this.$triggerGoogleTrackingEvent('GuestLeavesGroupOrder', 'GroupOrder');

            this.$triggerNewRelicEvent('GuestLeavesGroupOrder', 'Information', 'Guest Left Group Order');
          },
          title: 'Leave group order?'
        });
      },

      loadUpdatedOrder() {
        this.$store.dispatch('orders/getOrder', {
          silent: true
        });
      },

      removeMemberFromOrder(member) {
        this.$openModal('confirmation', {
          ctaConfirmText: 'Yes, Remove',
          primaryText: `${member.displayName} will be removed from the group. All of their items will be removed from the cart.`,
          onConfirm: () => this.$store.dispatch('orders/groupOrderRemoveMember', { member }),
          onConfirmSuccess: () => {
            // this.$triggerGoogleTrackingEvent('RemoveGuestFromGroupOrder', 'GroupOrder');

            this.$triggerNewRelicEvent('RemoveGuestFromGroupOrder', 'Information', 'Removed Guest From Group Order');
          },
          title: 'Remove from group?'
        });
      },

      async unlockOrder() {
        try {
          if (this.order.status === 'Created' && this.order.groupOrder.isLocked && this.order.groupOrder.you.isHost) {
            this.isUnlockingOrder = true;

            await this.$store.dispatch('orders/groupOrderUpdateLock', {
              isLocked: false
            });

            events.emit('notification', {
              type: 'success',
              message: 'Your group order has been unlocked.'
            });
          }
        }

        catch (error) {
          errorHandlers.generic(error);
        }

        finally {
          this.isUnlockingOrder = false;
        }
      }
    }
  };
</script>
